/**
 * SmartUpload — LLM-powered document upload and parsing (PDF/Word/text/CSV or pasted text).
 * Logic lives in smart-upload/useSmartUpload.js; inputs and results are presentational pieces.
 */
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Sparkles, Loader2 } from "lucide-react";
import { useSmartUpload } from "./smart-upload/useSmartUpload";
import { ModeSelector, ParseTypeGrid, InputArea } from "./smart-upload/SmartUploadInputs";
import { ParseResult } from "./smart-upload/SmartUploadResult";

const SmartUpload = ({ isOpen, onClose, onComplete, userId, instanceId, applyToInstance = true }) => {
  const u = useSmartUpload({ userId, instanceId, applyToInstance, onComplete, onClose });

  return (
    <Dialog open={isOpen} onOpenChange={u.handleClose}>
      <DialogContent className="bg-[#0a0a0a] border-gray-800 text-white max-w-2xl" data-testid="smart-upload-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl text-white flex items-center gap-2"><Sparkles className="h-5 w-5 text-ism-teal" /> Smart Import</DialogTitle>
          <DialogDescription className="text-gray-400">Upload documents or paste text. AI will automatically extract and organize the data.</DialogDescription>
        </DialogHeader>

        <ModeSelector mode={u.mode} setMode={u.setMode} />
        <ParseTypeGrid parseType={u.parseType} setParseType={u.setParseType} />
        <InputArea mode={u.mode} file={u.file} setFile={u.setFile} pastedText={u.pastedText} setPastedText={u.setPastedText} />
        <ParseResult result={u.result} />

        <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-gray-800">
          <Button variant="outline" onClick={u.handleClose} className="border-gray-700 text-gray-400" data-testid="smart-upload-close">Close</Button>
          <Button onClick={u.handleParse} disabled={!u.canParse} className="bg-ism-teal/20 text-ism-teal border border-ism-teal/30 hover:bg-ism-teal/30" data-testid="smart-upload-parse">
            {u.loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Analyzing...</> : <><Sparkles className="h-4 w-4 mr-2" /> Parse with AI</>}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SmartUpload;
